import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, collection, query, orderBy, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../services/firebase';
import Topbar from '../components/Topbar';
import Icon from '../components/Icon';
import { fmtDate, fmtInt, fmtUSD, localeOf } from '../i18n/format';
import './AccountHome.css';

const PLAN_OPTIONS = [
  { id: 'trial', label: 'Trial' },
  { id: 'starter', label: 'Starter' },
  { id: 'pro', label: 'Pro' },
];

// Last day of the current month as YYYY-MM-DD (suggested due date when releasing).
function endOfMonthISO() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
}

// Last N months as { value: 'YYYY-MM', label: 'jun 2026' } (UTC).
function recentMonths(n = 6) {
  const out = [];
  const d = new Date();
  d.setUTCDate(1);
  for (let i = 0; i < n; i++) {
    const value = d.toISOString().slice(0, 7);
    const label = d.toLocaleDateString(localeOf(), { month: 'short', year: 'numeric', timeZone: 'UTC' });
    out.push({ value, label });
    d.setUTCMonth(d.getUTCMonth() - 1);
  }
  return out;
}

export default function AccountHome({ accountId, role, isSysAdmin, onOpen, onBack, onLogout }) {
  const { t } = useTranslation();
  const [account, setAccount] = useState(null);
  const months = recentMonths();
  const [period, setPeriod] = useState(months[0].value);
  const [cost, setCost] = useState(null);
  const [costLoading, setCostLoading] = useState(false);

  const [ent, setEnt] = useState(null);
  const [payments, setPayments] = useState([]);
  const [subPlan, setSubPlan] = useState('starter');
  const [subPaidUntil, setSubPaidUntil] = useState(endOfMonthISO());
  const [subAmount, setSubAmount] = useState('');
  const [subNote, setSubNote] = useState('');
  const [subSaving, setSubSaving] = useState(false);
  const [subError, setSubError] = useState('');

  useEffect(() => {
    getDoc(doc(db, 'accounts', accountId)).then(s => setAccount(s.exists() ? s.data() : null));
  }, [accountId]);

  const loadEntitlements = () => {
    httpsCallable(functions, 'getEntitlements')({ accountId })
      .then(res => setEnt(res.data))
      .catch(() => setEnt(null));
  };

  useEffect(() => { loadEntitlements(); }, [accountId]);

  useEffect(() => {
    if (!isSysAdmin) return;
    getDocs(query(collection(db, 'accounts', accountId, 'payments'), orderBy('at', 'desc')))
      .then(snap => setPayments(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => setPayments([]));
  }, [accountId, isSysAdmin]);

  const releasePayment = async () => {
    setSubSaving(true);
    try {
      await httpsCallable(functions, 'setSubscription')({
        accountId,
        plan: subPlan,
        paidUntil: subPaidUntil,
        amount: subAmount ? Number(subAmount) : undefined,
        note: subNote || undefined,
      });
      setSubAmount('');
      setSubNote('');
      loadEntitlements();
      const snap = await getDocs(query(collection(db, 'accounts', accountId, 'payments'), orderBy('at', 'desc')));
      setPayments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
      setSubError(t('accountHome.subscription.error'));
    } finally {
      setSubSaving(false);
    }
  };

  useEffect(() => {
    if (!isSysAdmin) return;
    let active = true;
    setCostLoading(true);
    setCost(null);
    httpsCallable(functions, 'getAccountCost')({ accountId, period })
      .then(res => { if (active) setCost(res.data); })
      .catch(() => { if (active) setCost(null); })
      .finally(() => { if (active) setCostLoading(false); });
    return () => { active = false; };
  }, [accountId, period, isSysAdmin]);

  const isManager = role === 'owner' || role === 'admin';
  const cards = [
    { key: 'conversations', icon: 'message', title: t('accountHome.cards.conversationsTitle'), desc: t('accountHome.cards.conversationsDesc'), show: true },
    { key: 'prompt', icon: 'edit', title: t('accountHome.cards.promptTitle'), desc: t('accountHome.cards.promptDesc'), show: true },
    { key: 'channels', icon: 'phone', title: t('accountHome.cards.channelsTitle'), desc: t('accountHome.cards.channelsDesc'), show: isManager },
    { key: 'members', icon: 'users', title: t('accountHome.cards.membersTitle'), desc: t('accountHome.cards.membersDesc'), show: isManager },
  ];

  const u = cost?.units;
  const c = cost?.cost;

  return (
    <div className="page">
      <Topbar onBack={onBack} onLogout={onLogout} crumbs={account ? [account.name] : []} />
      <main className="page-main">
        <div className="home-head">
          <h1>{account?.name || t('accountHome.accountFallback')}</h1>
          <p className="home-sub">{role ? t(`accountHome.roles.${role}`) : ''}</p>
        </div>

        {ent?.subscription && (
          <div className={`plan-banner card ${ent.active ? '' : 'inactive'}`}>
            <span className="plan-ic"><Icon name="shield" size={18} /></span>
            <div className="plan-info">
              <strong>{t('accountHome.planLabel', { plan: ent.limits.label })}</strong>
              <span>
                {t(`accountHome.status.${ent.subscription.status}`, { defaultValue: ent.subscription.status })}
                {ent.subscription.paidUntil ? t('accountHome.validUntil', { date: fmtDate(ent.subscription.paidUntil) }) : ''}
              </span>
            </div>
            <span className={`plan-pill ${ent.active ? 'on' : 'off'}`}>{ent.active ? t('accountHome.activeBadge') : t('accountHome.inactiveBadge')}</span>
          </div>
        )}

        <div className="home-grid">
          {cards.filter(c => c.show).map(c => (
            <button className="home-card card" key={c.key} onClick={() => onOpen(c.key)}>
              <span className="home-ic"><Icon name={c.icon} size={22} /></span>
              <span className="home-ct">
                <strong>{c.title}</strong>
                <span>{c.desc}</span>
              </span>
              <Icon name="chevron-right" size={18} />
            </button>
          ))}
        </div>

        {isSysAdmin && (
          <section className="cost-section card">
            <div className="cost-head">
              <div className="cost-title">
                <span className="cost-ic"><Icon name="dollar" size={20} /></span>
                <div>
                  <strong>{t('accountHome.cost.title')}</strong>
                  <span>{t('accountHome.cost.subtitle')}</span>
                </div>
              </div>
              <select className="field cost-month" value={period} onChange={e => setPeriod(e.target.value)}>
                {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>

            {costLoading ? (
              <p className="cost-empty">{t('accountHome.cost.loading')}</p>
            ) : !cost || !cost.hasData ? (
              <p className="cost-empty">{t('accountHome.cost.noData')}</p>
            ) : (
              <>
                <div className="cost-total">
                  <span>{t('accountHome.cost.totalEstimated')}</span>
                  <strong>{fmtUSD(c.total)}</strong>
                </div>
                <div className="cost-grid">
                  <div className="cost-item">
                    <span className="cost-label">{t('accountHome.cost.chat')}</span>
                    <span className="cost-val">{fmtUSD(c.chat)}</span>
                    <span className="cost-sub">{t('accountHome.cost.chatSub', { in: fmtInt(u.inputTokens), out: fmtInt(u.outputTokens), requests: fmtInt(u.chatRequests) })}</span>
                  </div>
                  <div className="cost-item">
                    <span className="cost-label">{t('accountHome.cost.audio')}</span>
                    <span className="cost-val">{fmtUSD(c.audio)}</span>
                    <span className="cost-sub">{t('accountHome.cost.audioSub', { seconds: fmtInt(u.audioSeconds), requests: fmtInt(u.audioRequests) })}</span>
                  </div>
                  <div className="cost-item">
                    <span className="cost-label">{t('accountHome.cost.meta')}</span>
                    <span className="cost-val">{t('accountHome.cost.metaBillable', { count: fmtInt(u.metaBillable) })}</span>
                    <span className="cost-sub">
                      {t('accountHome.cost.metaSub', { sent: fmtInt(u.metaSent), service: fmtInt(u.metaByCategory.service), marketing: fmtInt(u.metaByCategory.marketing), utility: fmtInt(u.metaByCategory.utility), authentication: fmtInt(u.metaByCategory.authentication) })}
                    </span>
                  </div>
                </div>
                <p className="cost-foot">{t('accountHome.cost.footer', { version: cost.pricingVersion, currency: c.currency })}</p>
              </>
            )}
          </section>
        )}

        {isSysAdmin && (
          <section className="cost-section card">
            <div className="cost-head">
              <div className="cost-title">
                <span className="cost-ic"><Icon name="shield" size={20} /></span>
                <div>
                  <strong>{t('accountHome.subscription.title')}</strong>
                  <span>{t('accountHome.subscription.subtitle')}</span>
                </div>
              </div>
            </div>

            <div className="sub-form">
              <select className="field" value={subPlan} onChange={e => setSubPlan(e.target.value)}>
                {PLAN_OPTIONS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
              <input className="field" type="date" value={subPaidUntil} onChange={e => setSubPaidUntil(e.target.value)} title={t('accountHome.subscription.validUntil')} />
              <input className="field" type="number" min="0" step="0.01" placeholder={t('accountHome.subscription.amount')} value={subAmount} onChange={e => setSubAmount(e.target.value)} />
              <input className="field" placeholder={t('accountHome.subscription.note')} value={subNote} onChange={e => setSubNote(e.target.value)} />
              <button className="btn btn-primary" onClick={releasePayment} disabled={subSaving}>
                <Icon name="check" size={16} />{subSaving ? t('accountHome.subscription.releasing') : t('accountHome.subscription.release')}
              </button>
              {subError && <p className="form-error">{subError}</p>}
            </div>

            {payments.length > 0 && (
              <div className="sub-history">
                {payments.map(p => (
                  <div className="sub-row" key={p.id}>
                    <span className="sub-plan">{p.plan}</span>
                    <span className="sub-until">{t('accountHome.subscription.rowUntil', { date: fmtDate(p.paidUntil?.toMillis?.()) })}</span>
                    <span className="sub-amount">{p.amount != null ? `R$ ${Number(p.amount).toFixed(2)}` : t('common.dash')}</span>
                    <span className="sub-at">{p.at?.toDate ? fmtDate(p.at.toDate().getTime()) : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
